import { createContext, useContext } from "react";
import type { AppearanceSettings } from "./types";

export const DEFAULT_APPEARANCE: AppearanceSettings = {
  theme: "light",
  text_scale: 1.12,
};

export const AppearanceContext = createContext<{
  appearance: AppearanceSettings;
  save: (next: AppearanceSettings) => Promise<void>;
}>({ appearance: DEFAULT_APPEARANCE, save: async () => {} });

export const useAppearance = () => useContext(AppearanceContext);

/** Resolves "system" against the OS, so the rest of the app sees light or dark. */
function resolve(theme: AppearanceSettings["theme"]): "light" | "dark" {
  if (theme !== "system") return theme;
  return typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

// Mirrored in the browser so the very first paint — including the login
// screen, which happens before any API call can succeed — is already in the
// right theme. The server copy remains the source of truth; this is only to
// avoid a flash of the wrong one.
const CACHE_KEY = "tj.appearance";

export function applyCached(): void {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) apply({ ...DEFAULT_APPEARANCE, ...JSON.parse(cached) });
    else apply(DEFAULT_APPEARANCE);
  } catch {
    apply(DEFAULT_APPEARANCE);
  }
}

export function remember(appearance: AppearanceSettings): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(appearance));
  } catch {
    /* private window, or storage disabled — the server copy still works */
  }
}

export function apply(appearance: AppearanceSettings): void {
  const root = document.documentElement;
  root.dataset.theme = resolve(appearance.theme);
  root.style.setProperty("--text-scale", String(appearance.text_scale));
}


/** The label shown against each step of the size control. */
export const TEXT_SIZES = [
  { value: 1.0, label: "Compact" },
  { value: 1.12, label: "Default" },
  { value: 1.25, label: "Large" },
  { value: 1.4, label: "Larger" },
  { value: 1.55, label: "Largest" },
] as const;
